'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { cn } from '@/utils/cn';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import { useDispatch, useSelector } from 'react-redux';
import { getProductBrandUrl, getProductDetailUrl } from '@/url/product';
import { getProductTitleNameWithModelAndElement } from '@/utils/string';
import ProductStockStatus from '@/app/(products)/product/[slug]/components/ProductStockStatus';
import Link from 'next/link';
import { resetBreadcrumb } from '@/redux/reducers/breadcrumb-reducer';
import { resetProductFilters } from '@/redux/reducers/products-reducer';
import { setIsOpenGlobalSearchModal } from '@/redux/reducers/search-reducer';
import { prepareProductDetailSlug } from '@/utils/object';
import { cardViewTypes } from '@/constants/types';
import ProductCardCTA from './components/ProductCardCTA';
import ProductCardImage from './components/ProductCardImage';
import ProductCardTagList from './components/ProductCardTagList';
import ProductCardPrice from './components/ProductCardPrice';
import ProductCardRating from './components/ProductCardRating';
import TextExtraSmall from '../Typography/TextExtraSmall';
import TextBase from '../Typography/TextBase';

function ProductCardV2({ data, className = '', view = cardViewTypes.grid }) {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();

  const { width: screenWidth } = useWindowDimensions();

  const { items: cart } = useSelector((state) => state.carts);

  const { lang } = useSelector((state) => state.globalSettings);

  // states
  const [viewOption, setViewOption] = useState(cardViewTypes.grid);
  const [productInCart, setProductInCart] = useState(false);

  useEffect(() => {
    if (cart?.find((item) => item.id === data?.id)) {
      setProductInCart(true);
    } else {
      setProductInCart(false);
    }
  }, [cart]);

  useEffect(() => {
    if (
      pathname === '/products' ||
      pathname.startsWith('/category') ||
      pathname.startsWith('/brand')
    ) {
      setViewOption(view);
    } else {
      setViewOption(cardViewTypes.grid);
    }
  }, [view, pathname]);

  // handlers
  const handleDetailsView = (e) => {
    if (
      e.ctrlKey || // Windows/Linux: Ctrl + click
      e.metaKey || // Mac: Cmd + click
      e.button === 1 // Middle mouse button
    ) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    dispatch(setIsOpenGlobalSearchModal(false));
    dispatch(resetBreadcrumb());

    router.push(getProductDetailUrl(prepareProductDetailSlug(data)));
  };

  const handleBrandProductListingPage = (e) => {
    e.stopPropagation();
    dispatch(setIsOpenGlobalSearchModal(false));

    // reset filters before navigation to plp page
    dispatch(resetProductFilters());
  };

  const listView = viewOption === cardViewTypes.list;

  // styles
  const containerClasses = cn(
    'bg-pale-grey max-w-[160px] xs:max-w-full w-full max-w-full lg:max-w-[350px] lg:w-full shadow-trending-categories-search rounded-[7px] sm:rounded-xl relative overflow-hidden cursor-pointer  hover:shadow-card-hover transition-colors duration-200',
    {
      '!min-w-[325px] xs:!max-w-full sm:w-full flex justify-start': listView,
    },
    className,
  );
  const productInfoClasses = cn(
    'product-card-info-container p-10 lg:px-16 lg:pt-8 lg:pb-16',
    {
      'w-full sm:w-full flex flex-col sm:flex-row justify-start sm:justify-between items-start lg:items-center lg:py-[16px] lg:pl-[16px] lg:pr-[20px]':
        listView,
    },
  );

  const fullName = getProductTitleNameWithModelAndElement(data);

  if (data === null) {
    return null;
  }

  return (
    <Link
      prefetch={false}
      aria-label="Go to product details"
      passHref
      href={getProductDetailUrl(prepareProductDetailSlug(data))}
      onClick={(e) => handleDetailsView(e)}
      className="w-full"
    >
      <div className={cn('product-card', containerClasses)}>
        <ProductCardImage productData={data} viewType={viewOption} />

        <div className={productInfoClasses}>
          <div
            className={cn({
              'h-full flex flex-col justify-between': listView,
            })}
          >
            <div className="product-card-brand-and-name">
              {data?.brand?.name !== null ? (
                <Link
                  prefetch={false}
                  aria-label="Go to brand page"
                  passHref
                  href={getProductBrandUrl({ slug: data?.brand?.slug })}
                  onClick={(e) => handleBrandProductListingPage(e)}
                  className={cn('w-full')}
                >
                  <TextExtraSmall
                    text={data?.brand?.name[lang]}
                    className={cn(
                      'text-xs lg:text-xs text-coarse-wool font-medium tracking-[0.2px] lg:tracking-[0.24px] uppercase mb-3 lg:mb-6 hover:underline inline-block',
                      {
                        'lg:text-sm lg:leading-5': listView,
                      },
                    )}
                  />
                </Link>
              ) : null}

              <TextBase
                text={fullName}
                className={cn(
                  'max-w-[300px] xl:max-w-max w-auto text-coarse-wool font-semibold leading-20 tracking-[0.28px] lg:tracking-[0.32px] line-clamp-2 h-[40px]',
                  {
                    'lg:text-lg lg:leading-5 lg:tracking-[0.36px]': listView,
                  },
                )}
              />
            </div>

            <ProductStockStatus
              productData={data}
              isCard
              viewType={viewOption}
            />

            <ProductCardTagList
              tags={data?.tags || []}
              className={cn('hidden', {
                'hidden lg:flex flex-wrap items-center gap-[1px] lg:gap-1.5 sm:mb-0':
                  viewOption === cardViewTypes.list,
              })}
            />
          </div>

          <div
            className={cn('flex flex-col gap-2 lg:gap-2', {
              'lg:gap-x-[16px] xl:flex-row items-start xl:items-center':
                listView,
            })}
          >
            <div className="card-price-and-rating">
              <ProductCardPrice productData={data} viewType={viewOption} />

              <ProductCardRating
                currentRating={data?.current_rating}
                ratingCount={data?.rating_count}
              />
            </div>

            {/* badge in GRID_VIEW(<=1023px) */}
            <ProductCardTagList
              tags={data?.tags || []}
              className={cn(
                'items-center h-[24px] lg:h-[28px] gap-x-[2px] lg:gap-x-[6px]',
                {
                  'flex flex-wrap': viewOption === cardViewTypes.grid,
                },
                {
                  'flex flex-wrap lg:hidden': viewOption === cardViewTypes.list,
                },
              )}
              viewType={viewOption}
            />

            <div
              role="presentation"
              aria-label="product card action"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              className={cn(
                'product-card-action flex items-center justify-center hover:cursor-auto lg:mt-[6px]',
                {
                  'w-full mt-0': listView,
                },
              )}
            >
              <ProductCardCTA
                isSeeDetails={
                  !!(
                    Array.isArray(data?.sibling_elements) &&
                    data?.sibling_elements.length > 0
                  )
                }
                isStockLimited={data?.limited_stock}
                productData={data}
                onDetailsView={handleDetailsView}
                productInCart={productInCart}
                listView={listView}
                screenWidth={screenWidth}
                viewOption={viewOption}
              />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default ProductCardV2;
